import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { COUNTRIES } from 'cio/utils/countries';
import US_STATE_ABBREVIATIONS from 'cio/utils/us-state-abbreviations';
import { assert } from '@ember/debug';
import { tagName } from '@ember-decorators/component';

import { AccountAddressFormValues } from 'cio/utils/validations/account-address';
import { Task } from 'ember-concurrency';
import { next } from '@ember/runloop';

const TAX_CALCULATION_FIELDS = ['country', 'state', 'zipCode'];

/*
 * template-less base component class
 * extended by settings-account-address-form/subscription-form and settings-account-address-form/account-form:
 * representing component with same logic, similar copy, but somehow different HTML markup - mostly for styling reasons in specific context
 */
@tagName('')
export default class SettingsAccountAddressForm extends Component {
  readOnly = false;
  formValues!: AccountAddressFormValues;
  validateAttributeAction!: (field: string) => void;
  setFormValueAction!: (field: string, value: string) => void;
  errors!: { [field: string]: string[] };
  getUpdatedTaxRateTask!: Task<unknown, [{ country: string; state: string; zipCode: string }, boolean]>;

  _countrySelectOptions = [
    { label: 'Select a country…', value: '', disabled: true },
    ...COUNTRIES.map((c) => ({ label: c, value: c })),
  ];

  @computed('formValues.country')
  get _stateSelectOptions() {
    return this.formValues.country === 'United States' ? this._generateStateSelectOptions() : undefined;
  }
  _generateStateSelectOptions() {
    return [
      { value: '', label: 'US state', disabled: true },
      ...Object.entries(US_STATE_ABBREVIATIONS).map(([code]) => ({ label: code, value: code })),
    ];
  }

  didReceiveAttrs() {
    super.didReceiveAttrs();

    if (!this.formValues) {
      assert('Must supply @formValues param to settings-account-address-form component');
    }
  }

  @action
  setFormValue(field: keyof AccountAddressFormValues, value: string) {
    this.setFormValueAction(field, value);

    if (field === 'country' && value !== 'United States') {
      this.setFormValueAction('state', '');
    }

    // We need to wait for the `formValues` arg to be updated within this
    // this component, since we update the value a few lines above. This is
    // settled on the next tick
    next(() => {
      if (this.getUpdatedTaxRateTask && TAX_CALCULATION_FIELDS.includes(field)) {
        this.getUpdatedTaxRateTask.perform(
          {
            country: this.formValues.country,
            state: this.formValues.state,
            zipCode: this.formValues.zipCode,
            [field]: value,
          },
          field === 'zipCode', // debounce if updating the zipCode value
        );
      }
    });

    this.validateAttributeAction(field);
  }
}
